/**
 * Central API Client
 * ==================
 * Thin wrapper on top of `authFetch` (cookie-auth + CSRF) that adds:
 *   - JSON body serialisation + response parsing
 *   - Normalised error type (`ApiError`) carrying status + payload
 *   - Centralised 401 handling (redirects to /login once, debounced)
 *   - Single base-URL resolution (`process.env.REACT_APP_BACKEND_URL` →
 *     falls back to `window.location.origin` in dev/preview)
 *
 * Migration pattern: replace raw `fetch(`${url}/api/foo`)` blocks with
 * `apiClient.get('/api/foo')`. The new client returns parsed JSON
 * directly — no `await response.json()` step required.
 *
 *   // Before
 *   const r = await fetch(`${API_URL}/api/widgets/foo`, { credentials: 'include' });
 *   if (!r.ok) throw new Error('Failed');
 *   const data = await r.json();
 *
 *   // After
 *   const data = await apiClient.get('/api/widgets/foo');
 */
import { authFetch } from '@/utils/authFetch';
import { getBackendUrl } from '@/config/apiConfig';

const RESOLVED_BASE_URL = getBackendUrl();

let _redirectedTo401 = false;

export class ApiError extends Error {
  constructor(message, { status, payload, url }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
    this.url = url;
  }
}

/**
 * Reset the internal 401-redirect debounce.
 * Tests can call this between cases to restart the guard.
 */
export function _resetAuthRedirectGuard() {
  _redirectedTo401 = false;
}

function buildUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  const prefix = path.startsWith('/') ? '' : '/';
  return `${RESOLVED_BASE_URL}${prefix}${path}`;
}

function maybeRedirectOn401(status) {
  if (status !== 401 || _redirectedTo401 || typeof window === 'undefined') return;
  // Don't bounce already-public pages or the login page itself
  const path = window.location.pathname || '';
  if (path === '/' || path.startsWith('/login') || path.startsWith('/register') || path.startsWith('/public') || path.startsWith('/industries') || path === '/pricing' || path === '/about' || path === '/contact') {
    return;
  }
  _redirectedTo401 = true;
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/login?next=${next}`;
}

async function parseResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  if (response.status === 204) return null;
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  return await response.text();
}

async function request(method, path, { body, headers, signal, raw = false } = {}) {
  const url = buildUrl(path);
  const reqHeaders = { ...(headers || {}) };
  let serialisedBody;

  if (body !== undefined && body !== null) {
    if (body instanceof FormData || body instanceof Blob || typeof body === 'string') {
      serialisedBody = body;
    } else {
      reqHeaders['Content-Type'] = reqHeaders['Content-Type'] || 'application/json';
      serialisedBody = JSON.stringify(body);
    }
  }

  const response = await authFetch(url, {
    method,
    headers: reqHeaders,
    body: serialisedBody,
    signal,
  });

  if (raw) return response;

  if (!response.ok) {
    maybeRedirectOn401(response.status);
    const payload = await parseResponse(response);
    const detail =
      (payload && typeof payload === 'object' && (payload.detail || payload.message)) ||
      response.statusText ||
      `HTTP ${response.status}`;
    throw new ApiError(detail, { status: response.status, payload, url });
  }

  return parseResponse(response);
}

export const apiClient = {
  get: (path, opts) => request('GET', path, opts),
  post: (path, body, opts) => request('POST', path, { ...(opts || {}), body }),
  put: (path, body, opts) => request('PUT', path, { ...(opts || {}), body }),
  patch: (path, body, opts) => request('PATCH', path, { ...(opts || {}), body }),
  delete: (path, opts) => request('DELETE', path, opts),
  raw: (method, path, opts) => request(method, path, { ...(opts || {}), raw: true }),
};

export default apiClient;
